import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, Grid, Select, MenuItem, Button, Chip, Tooltip, Table, TableHead,
  TableRow, TableCell, TableBody, TableSortLabel, TextField, InputAdornment, ToggleButton, ToggleButtonGroup, Menu, Checkbox,
  FormControlLabel, Dialog, DialogTitle, DialogContent, IconButton, Collapse, Alert, Skeleton, Link,
  TablePagination, CircularProgress, Avatar, Drawer, Divider,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import * as XLSX from 'xlsx';
import { analyticsApi } from '../services/api';
import PeriodPicker from '../components/common/PeriodPicker';
import { EXPLAIN } from '../utils/explain';
import ComingSoon from '../components/common/ComingSoon';
import SubTabs from '../components/common/SubTabs';
import InfoTooltip from '../components/common/InfoTooltip';
import StockChart from '../components/common/StockChart';
import EmptyState from '../components/common/EmptyState';
import { ErrorState } from '../components/common/States';
import { periodFromSearch, periodQuery, periodSearch } from '../utils/period';
import { formatMoney as formatMoneyBase, formatNumber, formatPercent, formatDate, formatRange, apiError, pluralize, MISSING_LABELS } from '../utils/format';
import { tokens } from '../theme';
import { toneColor } from '../utils/tone';
import { moveColumn } from '../utils/columns';
import { mpPath } from '../utils/marketplaces';
import ExportGate from '../components/common/ExportGate';
import { abcAnalysis, ABC_CLASSES } from '../utils/abc';

// Все суммы в аналитике — с копейками, как в отчётах WB.
const formatMoney = (v, opts) => formatMoneyBase(v, { kopecks: true, ...opts });

// v5: the saved list is also the column order (drag a header to move it).
// v4 kept only which columns are on; it is carried over once in the default order.
const COLS_KEY = 'marqit_master_columns_v5';
const COLS_KEY_V4 = 'marqit_master_columns_v4';

const HEADLINE = [
  { key: 'net_profit', label: 'Итоговая прибыль', hint: 'К перечислению − реклама со «Счёта» продвижения − себестоимость − налоги. Комиссия WB уже удержана из суммы к перечислению.' },
  { key: 'revenue', label: 'Выручка', hint: 'Цена продавца с учётом его скидок («Цена розничная с учётом согласованной скидки»), за вычетом возвратов. База для комиссии WB.' },
  { key: 'realized', label: 'WB реализовал товар', hint: 'Сколько фактически заплатили покупатели («Вайлдберриз реализовал товар (Пр)»), с учётом скидок WB за счёт площадки. Разница с выручкой не уменьшает доход продавца.' },
  { key: 'gross_profit', label: 'Валовая прибыль', hint: 'Выручка − себестоимость проданных товаров (по себестоимости на дату продажи).' },
  { key: 'to_pay', label: 'К перечислению', hint: 'Итого к оплате по отчётам WB: к перечислению за товар + компенсации и коррекции (включая компенсацию скидки лояльности) − логистика − хранение − приёмка − штрафы − удержания − минимальный платёж и прочие услуги WB. Не равно окончательной прибыли бизнеса.' },
  { key: 'commission', label: 'Комиссия WB', hint: 'Выручка − К перечислению за товар: вознаграждение WB (кВВ) и платёжные услуги. Процент — от выручки по цене продавца.' },
  { key: 'logistics', label: 'Логистика', hint: 'Услуги по доставке товара покупателю и обратно, коррекции логистики.' },
  { key: 'storage', label: 'Хранение', hint: 'Платное хранение по отчёту реализации (с пересчётами).' },
  { key: 'acceptance', label: 'Приёмка', hint: 'Операции при приёмке и обработка товара.' },
  { key: 'penalties_other', label: 'Штрафы и прочие расходы', hint: 'Штрафы + удержания + корректировка вознаграждения WB + программа лояльности + изменение срока перечисления + реклама со «Счёта» продвижения.' },
];

// Headline figures that are a result for the seller: coloured by sign. Costs stay neutral.
const RESULT_KEYS = ['net_profit', 'gross_profit', 'to_pay'];

const STICKY_HEAD = { position: 'sticky', left: 0, zIndex: 4, bgcolor: 'background.paper', boxShadow: `1px 0 0 ${tokens.border}` };
const STICKY_CELL = { position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper', boxShadow: `1px 0 0 ${tokens.border}` };

const PRODUCT_COLUMNS = [
  { key: 'vendor_code', label: 'Артикул продавца', default: false, hint: 'Артикул, который вы задали в кабинете WB.' },
  { key: 'tech_size', label: 'Размер', default: true, sizeOnly: true, hint: 'Размер товара. Показывается при группировке «Артикул и размер».' },
  { key: 'cost_price', label: 'Себестоимость', money: true, default: false, hint: 'Себестоимость единицы, которую вы указали на вкладке «Себестоимость и налоги». Не задана — прибыль и ROI не считаются.' },
  { key: 'orders', label: 'Заказы', num: true, default: false, hint: 'Количество заказов за период по дате заказа (оперативные данные WB, не финансовый отчёт).' },
  { key: 'cancelled', label: 'Отмены', num: true, default: false, hint: 'Заказы за период, которые были отменены. Считаются по дате заказа.' },
  { key: 'sold', label: 'Продажи', num: true, default: true, hint: 'Штуки, проданные за период, по операциям финансовых отчётов WB. Возвраты вычитаются отдельно.' },
  { key: 'returned', label: 'Возвраты', num: true, tone: 'expense', default: false, hint: 'Штуки, возвращённые покупателями за период, по операциям финансовых отчётов WB.' },
  { key: 'buyout_percent', label: '% выкупа', pct: true, default: false, hint: 'Продажи / (продажи + возвраты) × 100% по операциям периода. Это не доля заказов периода.' },
  { key: 'revenue', label: 'Выручка', money: true, default: true, hint: 'Цена продавца с учётом ваших скидок за проданные товары минус возвраты. От неё WB считает комиссию.' },
  { key: 'commission', label: 'Комиссия', money: true, tone: 'expense', default: false, hint: 'Выручка − сумма к перечислению за товар: вознаграждение WB и платёжные услуги.' },
  { key: 'logistics', label: 'Логистика', money: true, tone: 'expense', default: false, hint: 'Доставка товара покупателю и обратно, коррекции логистики по этому артикулу.' },
  { key: 'penalties', label: 'Штрафы', money: true, tone: 'expense', default: false, hint: 'Штрафы WB, начисленные на этот артикул за период.' },
  { key: 'paid_storage', label: 'Хранение (отчёт WB)', money: true, tone: 'expense', default: true, detail: 'storage', hint: 'Платное хранение по отчёту WB о платном хранении, по артикулу. Складывается по дням хранения. Справочно: в прибыль товара входит хранение из отчёта реализации.' },
  { key: 'paid_acceptance', label: 'Приёмка (отчёт WB)', money: true, tone: 'expense', default: false, detail: 'acceptance', hint: 'Платная приёмка по отчёту WB о платной приёмке, по артикулу. Справочно: в прибыль входит приёмка из отчёта реализации.' },
  { key: 'ad_spend', label: 'Реклама', money: true, tone: 'expense', default: true, detail: 'ads', hint: 'Расход на рекламу по статистике кампаний WB за дни показа рекламы. Справочно: в прибыль товара не входит, итоговая прибыль вычитает реклама по фактическим списаниям.' },
  { key: 'drr', label: 'ДРР', pct: true, default: true, detail: 'ads', hint: 'ДРР = реклама / выручка × 100%. Доля выручки, которую вы отдали на рекламу. Справочно.' },
  { key: 'expenses', label: 'Расходы WB', money: true, tone: 'expense', default: false, hint: 'Комиссия + логистика + хранение + приёмка + штрафы + удержания по этому артикулу.' },
  { key: 'cost_of_goods', label: 'Себестоимость продаж', money: true, tone: 'expense', default: false, hint: 'Себестоимость проданных единиц на дату продажи, за вычетом возвращённых.' },
  { key: 'profit', label: 'Прибыль', money: true, tone: 'signed', default: true, hint: 'К перечислению за товар − логистика − хранение − приёмка − штрафы − удержания − себестоимость проданного. Реклама и налоги не вычитаются.' },
  { key: 'margin', label: 'Маржинальность', pct: true, tone: 'signed', default: true, hint: 'Прибыль / выручка × 100%. Сколько копеек прибыли остаётся с каждого рубля выручки.' },
  { key: 'roi', label: 'ROI', pct: true, tone: 'signed', default: true, hint: 'ROI = прибыль / себестоимость проданных товаров × 100%. Сколько прибыли принёс вложенный в закупку рубль: вложили 1000 ₽, заработали 500 ₽ — ROI 50%. Реклама и налоги в прибыль не входят. Без себестоимости не считается.' },
  { key: 'stock', label: 'Остаток', num: true, default: true, hint: 'Сколько штук товара сейчас лежит на складах WB (последнее обновление остатков).' },
];

// ROI is derived from the fields the report already has; null without cost of goods or profit.
const withRoi = (p) => {
  const cogs = Number(p.cost_of_goods);
  return { ...p, roi: p.profit == null || p.cost_of_goods == null || !(cogs > 0) ? null : (Number(p.profit) / cogs) * 100 };
};

const readCols = () => {
  try {
    const v = JSON.parse(window.localStorage.getItem(COLS_KEY));
    if (Array.isArray(v)) return v.filter((k) => PRODUCT_COLUMNS.some((c) => c.key === k));
    const old = JSON.parse(window.localStorage.getItem(COLS_KEY_V4));
    if (Array.isArray(old)) return PRODUCT_COLUMNS.filter((c) => old.includes(c.key)).map((c) => c.key);
  } catch (e) {
    // defaults below
  }
  return PRODUCT_COLUMNS.filter((c) => c.default).map((c) => c.key);
};

const headlineValue = (h, key) => {
  switch (key) {
    case 'commission':
      return h.commission;
    case 'penalties_other':
      return Number(h.penalties) + Number(h.other_expenses) + Number(h.ad_account);
    default:
      return h[key];
  }
};

// "Хранение и приёмка (отчёт WB)": where the per-article figures come from and how far they reach.
// A detail report that covers only part of the period: "данные по 26.09.2026" / "данные с 01.09.2026".
const partialNote = (d, period) => {
  if (!d || d.status === 'none') return 'отчёт WB ещё не загружен';
  if (d.status !== 'partial') return null;
  const parts = [];
  if (period && d.from > period.from) parts.push(`с ${formatDate(d.from)}`);
  if (!period || d.to < period.to) parts.push(`по ${formatDate(d.to)}`);
  return `данные ${parts.join(' ')}`;
};

const detailCaption = (details, period) => {
  if (!details) return '';
  const gaps = [['хранение', details.storage], ['приёмка', details.acceptance], ['реклама', details.ads]]
    .map(([name, d]) => { const n = partialNote(d, period); return n ? `${name} — ${n}` : null; })
    .filter(Boolean);
  return 'Хранение и приёмка (отчёт WB) — по отчётам WB о платном хранении и платной приёмке по артикулам. '
    + 'Реклама — расход по статистике рекламных кампаний WB, ДРР = реклама / выручка. '
    + 'Эти колонки справочные и в прибыль по товару не входят: реклама вычитается из итоговой прибыли по фактическим списаниям.'
    + (gaps.length ? ` Покрытие: ${gaps.join('; ')}.` : '');
};

const DETAIL_MISSING = {
  storage: 'Отчёт WB о платном хранении за период ещё не загружен',
  acceptance: 'Отчёт WB о платной приёмке за период ещё не загружен',
  ads: 'Статистика рекламных кампаний за период ещё не загружена',
};

// Advertising by article against the actual charges of the ad cabinet for the same period.
const AdsCheckStrip = ({ check }) => {
  if (!check || check.stats?.status === 'none') return null;
  const pct = check.difference_percent == null ? null : Number(check.difference_percent);
  const noticeable = pct != null && pct > 5 && Math.abs(Number(check.difference)) >= 100;
  const item = (label, value, color) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
      <Typography sx={{ fontWeight: 700, whiteSpace: 'nowrap', color }}>{value}</Typography>
    </Box>
  );
  const strip = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }} alignItems={{ sm: 'center' }}
      sx={{ px: 2, py: 1.25, borderRadius: '12px', border: `1px solid ${noticeable ? tokens.warning + '55' : tokens.border}`,
        bgcolor: noticeable ? tokens.warningSoft : tokens.bg }}>
      {item('Реклама по артикулам', formatMoney(check.by_articles, { kopecks: true }))}
      {item('Списано в рекламном кабинете', check.charges?.status === 'none' ? 'не загружено' : formatMoney(check.charged, { kopecks: true }))}
      {check.charges?.status !== 'none' && item('Расхождение',
        `${formatMoney(check.difference, { kopecks: true })}${pct != null ? ` · ${formatPercent(pct)}` : ''}`,
        noticeable ? tokens.warning : undefined)}
      <Box sx={{ flex: 1 }} />
      <InfoTooltip exclamation size={20} title={EXPLAIN.adsCheck.short} heading={EXPLAIN.adsCheck.title} details={EXPLAIN.adsCheck.details} sources={EXPLAIN.adsCheck.sources} />
    </Stack>
  );
  const diff = Number(check.difference);
  const hasCharges = check.charges?.status !== 'none';
  const explain = !hasCharges
    ? 'Списания рекламного кабинета за период ещё не загружены, сравнить нечего.'
    : `«Реклама по артикулам» — сколько кампании потратили по статистике WB, разнесено по товарам. «Списано в рекламном кабинете» — сколько WB реально снял с вашего рекламного счёта за тот же период. Расхождение = первое минус второе. ${
      diff < 0 ? 'Минус: списано больше, чем показала статистика по товарам. Обычно так бывает, когда WB списывает оплату пакетами и позже, либо есть удалённые кампании или кампании без привязки к товару.'
        : diff > 0 ? 'Плюс: по статистике потрачено больше, чем списано. Обычно так бывает, когда часть расхода WB спишет позже.' : 'Суммы совпадают.'
    } В прибыль идёт фактическое списание, а не статистика.`;
  return (
    <Box sx={{ mb: 1.5 }}>
      {strip}
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75, px: 0.5 }}>{explain}</Typography>
    </Box>
  );
};

const PAYMENT_TYPES = { cpm: 'оплата за показы', cpc: 'оплата за клики' };
const campaignName = (c) => c.name || `Кампания № ${c.advert_id}`;

// Paid storage by warehouse, paid acceptance by supply and advertising by campaign for one article
// (top of its operations).
const ProductChargesPanel = ({ params }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    setData(null);
    setError('');
    analyticsApi.productCharges(params).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, [params]);
  if (error) return <Box sx={{ mb: 2 }}><ErrorState message={error} compact /></Box>;
  if (!data) return <Skeleton variant="rounded" height={120} sx={{ mb: 2, borderRadius: '12px' }} />;
  const src = data.sources || {};
  const note = (d) => partialNote(d, data.period);
  const box = (title, total, sub, source, children) => (
    <Box sx={{ flex: 1, minWidth: 0, p: 2, borderRadius: '12px', border: `1px solid ${tokens.border}`, bgcolor: tokens.bg }}>
      <Typography variant="body2" color="text.secondary">{title}</Typography>
      <Typography sx={{ fontWeight: 700, fontSize: '1.35rem' }}>{source?.status === 'none' ? '—' : formatMoney(total, { kopecks: true })}</Typography>
      <Typography variant="caption" color="text.secondary" component="div">{[sub, note(source)].filter(Boolean).join(' · ')}</Typography>
      <Box sx={{ mt: 1 }}>{children}</Box>
    </Box>
  );
  const row = (k, label, value) => (
    <Stack key={k} direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.5, borderTop: `1px solid ${tokens.border}` }}>
      <Typography variant="body2" sx={{ minWidth: 0 }} noWrap title={label}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{value}</Typography>
    </Stack>
  );
  const campaigns = data.ad_campaigns || [];
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Хранение, приёмка и реклама по артикулу (отчёты и статистика WB) <InfoTooltip title={'Платное хранение и платная приёмка — из отчётов WB, реклама — из статистики кампаний. Суммы справочные: в прибыль товара входят хранение и приёмка из отчёта реализации, а реклама вычитается в итоговой прибыли по фактическим списаниям.'} size={14} exclamation /></Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        {box('Платное хранение', data.storage, data.storage_days ? `${data.storage_days} дн. хранения за период` : 'за период не начислялось', src.storage,
          data.storage_warehouses.slice(0, 6).map((w) => row(w.warehouse, `${w.warehouse} · ${w.days} дн.`, formatMoney(w.amount, { kopecks: true }))))}
        {box('Платная приёмка', data.acceptance, data.acceptance_items.length ? `поставок: ${data.acceptance_items.length}` : 'за период не начислялась', src.acceptance,
          data.acceptance_items.slice(0, 6).map((a) => row(`${a.date}-${a.income_id}`, `${formatDate(a.date)} · поставка ${a.income_id || '—'} · ${formatNumber(a.count)} шт.`, formatMoney(a.amount, { kopecks: true }))))}
        {box('Реклама', data.ad_spend, campaigns.length ? `${campaigns.length} ${pluralize(campaigns.length, 'кампания', 'кампании', 'кампаний')} за период` : 'за период не рекламировался', src.ads,
          campaigns.slice(0, 3).map((c) => row(c.advert_id, campaignName(c), formatMoney(c.spend, { kopecks: true }))))}
      </Stack>
      {campaigns.length > 0 && (
        <Box sx={{ mt: 1.5, overflowX: 'auto', border: `1px solid ${tokens.border}`, borderRadius: '12px' }}>
          <Table size="small" aria-label="Реклама по кампаниям">
            <TableHead>
              <TableRow>
                <TableCell>Кампания</TableCell>
                <TableCell align="right">Показы</TableCell>
                <TableCell align="right">Клики</TableCell>
                <TableCell align="right">CTR</TableCell>
                <TableCell align="right">CPC</TableCell>
                <TableCell align="right">Заказы</TableCell>
                <TableCell align="right">Сумма заказов</TableCell>
                <TableCell align="right">Расход</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((c) => (
                <TableRow key={c.advert_id}>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" noWrap title={campaignName(c)}>{campaignName(c)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      № {c.advert_id}{c.payment_type ? ` · ${PAYMENT_TYPES[c.payment_type] || c.payment_type}` : ''} · {c.days} {pluralize(c.days, 'день', 'дня', 'дней')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatNumber(c.views)}</TableCell>
                  <TableCell align="right">{formatNumber(c.clicks)}</TableCell>
                  <TableCell align="right">{c.ctr == null ? '—' : formatPercent(c.ctr)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{c.cpc == null ? '—' : formatMoney(c.cpc, { kopecks: true })}</TableCell>
                  <TableCell align="right">{formatNumber(c.orders)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatMoney(c.order_sum)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{formatMoney(c.spend, { kopecks: true })}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Справочно: в прибыли хранение и приёмка учтены по отчёту реализации, где WB обычно начисляет их без привязки к товару,
        а реклама — по фактическим списаниям рекламного кабинета. Расход и заказы по кампаниям — из статистики WB по дням показа рекламы.
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------- operations dialog

const OperationsList = ({ open, params }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  // the page belongs to the period and cabinet it was chosen for: new params start at page 0
  const [paging, setPaging] = useState({ params, page: 0 });
  const page = paging.params === params ? paging.page : 0;
  const setPage = (p) => setPaging({ params, page: p });
  const [openRow, setOpenRow] = useState(null);
  const rowsPerPage = 50;

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setData(null);
    setError('');
    analyticsApi.operations({ ...params, limit: rowsPerPage, offset: page * rowsPerPage })
      .then((r) => live && setData(r.data))
      .catch((e) => live && setError(apiError(e)));
    return () => { live = false; };
  }, [open, params, page]);

  return (
    <>
        {error && <ErrorState message={error} compact />}
        {!data && !error && <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>}
        {data && data.items.length === 0 && <EmptyState title="Операций нет" description="За выбранный период таких операций в отчётах WB нет." />}
        {data && data.items.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Дата</TableCell>
                  <TableCell>Обоснование WB</TableCell>
                  <TableCell>Причина / вид операции (WB)</TableCell>
                  <TableCell>Товар</TableCell>
                  <TableCell align="right">Сумма</TableCell>
                  <TableCell>Отчёт</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((o) => {
                  const amount = [o.penalty, o.deduction, o.logistics, o.storage, o.acceptance, o.additional_payment].find((v) => Number(v) !== 0)
                    ?? (Number(o.for_pay) !== 0 ? o.for_pay : o.retail_amount);
                  return (
                    <React.Fragment key={o.rrd_id}>
                      <TableRow hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(o.date)}</TableCell>
                        <TableCell>
                          {o.operation || '—'}
                          {o.doc_type && <Typography variant="caption" color="text.secondary" display="block">{o.doc_type}</Typography>}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          {o.reason ? o.reason : <Typography variant="body2" color="text.secondary">WB не передал причину</Typography>}
                          {o.explanation && <Typography variant="caption" color="text.secondary" display="block">{o.explanation}</Typography>}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 240 }}>
                          {o.nm_id ? (
                            <>
                              <Typography variant="body2" noWrap>{o.title || o.vendor_code || `Артикул ${o.nm_id}`}</Typography>
                              <Typography variant="caption" color="text.secondary">WB {o.nm_id}{o.tech_size ? ` · ${o.tech_size}` : ''}</Typography>
                            </>
                          ) : <Typography variant="body2" color="text.secondary">без привязки к товару</Typography>}
                          {o.srid && <Typography variant="caption" color="text.secondary" display="block">Заказ {o.srid}</Typography>}
                          {o.supply_id > 0 && <Typography variant="caption" color="text.secondary" display="block">Поставка {o.supply_id}{o.supply_status ? ` (${o.supply_status})` : ''}</Typography>}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{formatMoney(amount, { kopecks: true })}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          № {o.report_id}
                          <Typography variant="caption" color="text.secondary" display="block">{o.period === 'daily' ? 'ежедневный' : 'еженедельный'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Исходная строка WB">
                            <IconButton size="small" onClick={() => setOpenRow(openRow === o.rrd_id ? null : o.rrd_id)} aria-expanded={openRow === o.rrd_id} aria-label="Показать исходную строку">
                              <ExpandMoreRoundedIcon sx={{ transform: openRow === o.rrd_id ? 'rotate(180deg)' : 'none' }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0, border: openRow === o.rrd_id ? undefined : 'none' }}>
                          <Collapse in={openRow === o.rrd_id} unmountOnExit>
                            <Box component="pre" sx={{ m: 0, p: 2, bgcolor: tokens.primaryTint, fontSize: '0.75rem', overflowX: 'auto' }}>
                              {JSON.stringify({ rrdId: o.rrd_id, ...o.raw }, null, 2)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      {data && data.total > rowsPerPage && (
        <TablePagination component="div" count={data.total} page={page} rowsPerPage={rowsPerPage} rowsPerPageOptions={[rowsPerPage]}
          onPageChange={(_, p) => setPage(p)} labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`} />
      )}
    </>
  );
};

const OperationsDialog = ({ open, onClose, params, title }) => (
  <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      {title}
      <IconButton onClick={onClose} aria-label="Закрыть"><CloseRoundedIcon /></IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <OperationsList open={open} params={params} />
    </DialogContent>
  </Dialog>
);

// ---------------------------------------------------------------- product drawer

const columnHint = (key) => PRODUCT_COLUMNS.find((c) => c.key === key)?.hint;

const kpi = (label, value, color, hint) => (
  <Box key={label} sx={{ p: 1.5, borderRadius: '12px', bgcolor: tokens.primaryTint }}>
    <Typography variant="caption" color="text.secondary">{label}{hint && <> <InfoTooltip title={hint} size={14} exclamation /></>}</Typography>
    <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color }}>{value}</Typography>
  </Box>
);

const ProductDrawer = ({ product, params, onClose }) => {
  const p = product ? withRoi(product) : null;
  const [stock, setStock] = useState(null);
  const nmId = p?.nm_id;
  const chargeParams = useMemo(() => (nmId ? { ...params, nm_id: nmId } : null), [params, nmId]);
  const opsParams = useMemo(() => (nmId ? { ...params, filter: 'all', nm_id: nmId } : null), [params, nmId]);

  useEffect(() => {
    setStock(null);
    if (!nmId) return;
    let live = true;
    analyticsApi.stocks({ days: 30, ...(params.account_id ? { account_id: params.account_id } : {}) })
      .then((r) => { if (live) setStock((r.data.items || []).find((i) => i.nm_id === nmId) || { history: [] }); })
      .catch(() => { if (live) setStock({ history: [] }); });
    return () => { live = false; };
  }, [nmId, params.account_id]);

  const num = (v, fmt) => (v == null ? '—' : fmt(v));
  return (
    <Drawer anchor="right" open={Boolean(p)} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 760 }, maxWidth: '100%' } }}>
      {p && (
        <Box sx={{ p: { xs: 2, md: 3 } }} role="dialog" aria-label={`Разбор товара: ${p.title || p.nm_id}`}>
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2.5 }}>
            <Avatar variant="rounded" src={p.photo_url || undefined} alt="" sx={{ width: 64, height: 84, bgcolor: tokens.primaryTint, color: tokens.inkMuted }}>
              <ImageOutlinedIcon />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" sx={{ lineHeight: 1.25 }}>{p.title || 'Без названия'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {[p.vendor_code, `WB ${p.nm_id}`, p.brand].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label="Закрыть"><CloseRoundedIcon /></IconButton>
          </Stack>

          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, mb: 3 }}>
            {kpi('Продажи', `${formatNumber(p.sold)} шт.`, undefined, columnHint('sold'))}
            {kpi('Выручка', formatMoney(p.revenue), undefined, columnHint('revenue'))}
            {kpi('Прибыль', p.profit == null ? 'нет себест.' : formatMoney(p.profit), p.profit == null ? tokens.inkMuted : toneColor(p.profit), columnHint('profit'))}
            {kpi('Маржинальность', num(p.margin, formatPercent), p.margin == null ? undefined : toneColor(p.margin), columnHint('margin'))}
            {kpi('ROI', num(p.roi, formatPercent), p.roi == null ? undefined : toneColor(p.roi), columnHint('roi'))}
            {kpi('Остаток', stock ? `${formatNumber(stock.quantity ?? p.stock ?? 0)} шт.` : num(p.stock, (v) => `${formatNumber(v)} шт.`), undefined, columnHint('stock'))}
          </Box>

          <ProductChargesPanel params={chargeParams} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Остаток по дням <InfoTooltip title="Сколько штук товара лежало на складах WB на конец каждого дня за последние 30 дней. Дни без снимка остатков пропускаются." size={14} exclamation /></Typography>
          {stock ? <StockChart points={stock.history || []} height={180} label={`Остаток товара ${p.nm_id} по дням`} /> : <Skeleton variant="rounded" height={180} sx={{ borderRadius: '12px' }} />}

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Операции по товару <InfoTooltip title="Строки финансовых отчётов WB по этому товару за выбранный период: продажи, возвраты, логистика, штрафы и другие начисления. Стрелка справа показывает исходную строку WB." size={14} exclamation /></Typography>
          <OperationsList open params={opsParams} />
        </Box>
      )}
    </Drawer>
  );
};

// ---------------------------------------------------------------- sections

const SectionRow = ({ s, onShowOps }) => {
  const [open, setOpen] = useState(false);
  const expandable = (s.items && s.items.length > 0) || (s.lines && s.lines.length > 0) || s.note;
  const color = s.kind === 'income' ? toneColor(s.amount) : s.kind === 'expense' ? toneColor(s.amount, 'expense') : tokens.inkSoft;
  return (
    <Box sx={{ borderBottom: `1px solid ${tokens.border}`, '&:last-child': { borderBottom: 'none' } }}>
      <Box
        component={expandable ? 'button' : 'div'}
        type={expandable ? 'button' : undefined}
        onClick={() => expandable && setOpen((v) => !v)}
        aria-expanded={expandable ? open : undefined}
        sx={{
          all: 'unset', boxSizing: 'border-box', width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, py: 1.75, px: 1,
          cursor: expandable ? 'pointer' : 'default', borderRadius: 2,
          '&:hover': expandable ? { bgcolor: tokens.primaryTint } : undefined,
          '&:focus-visible': { boxShadow: `0 0 0 3px ${tokens.brand}66` },
        }}
      >
        <ExpandMoreRoundedIcon sx={{ color: expandable ? tokens.inkSoft : 'transparent', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
        <Typography sx={{ flex: 1, fontWeight: 600 }}>{s.label}</Typography>
        {s.formula && <Box onClick={(e) => e.stopPropagation()}><InfoTooltip title={s.formula} /></Box>}
        <Typography sx={{ fontWeight: 700, color, minWidth: 130, textAlign: 'right', whiteSpace: 'nowrap' }}>
          {s.key === 'informational' ? '' : formatMoney(s.amount, { kopecks: true })}
        </Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 6, pr: 1, pb: 2 }}>
          {s.note && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{s.note}</Typography>}
          {s.lines?.map((l) => (
            <Stack key={l.key} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2">{l.label}</Typography>
                {l.formula && <InfoTooltip title={l.formula} size={14} />}
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatMoney(l.amount, { kopecks: true })}</Typography>
            </Stack>
          ))}
          {s.items?.length > 0 && (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Обоснование WB</TableCell>
                  <TableCell>Вид / причина (WB)</TableCell>
                  <TableCell align="right">Операций</TableCell>
                  <TableCell align="right">Сумма</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {s.items.slice(0, 30).map((it, i) => (
                  <TableRow key={i}>
                    <TableCell>{it.operation || '—'}</TableCell>
                    <TableCell sx={{ color: it.reason ? 'text.primary' : 'text.secondary' }}>{it.reason || 'не указано WB'}</TableCell>
                    <TableCell align="right">{formatNumber(it.rows)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatMoney(it.amount, { kopecks: true })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {s.items?.length > 30 && <Typography variant="caption" color="text.secondary">Показаны 30 крупнейших групп из {s.items.length}.</Typography>}
          {s.filter && (
            <Button size="small" startIcon={<ListAltRoundedIcon />} sx={{ mt: 1 }} onClick={() => onShowOps(s.filter, s.label)}>
              Исходные операции
            </Button>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ---------------------------------------------------------------- products

const ProductsTable = ({ report, group, onGroup, onShowProduct }) => {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [size, setSize] = useState('all');
  const [sort, setSort] = useState({ key: 'revenue', dir: 'desc' });
  const [cols, setCols] = useState(readCols);
  const [colMenu, setColMenu] = useState(null);

  const saveCols = (next) => {
    setCols(next);
    try {
      window.localStorage.setItem(COLS_KEY, JSON.stringify(next));
    } catch (e) {
      // not persisted in private mode
    }
  };
  const visible = cols
    .map((k) => PRODUCT_COLUMNS.find((c) => c.key === k))
    .filter((c) => c && (!c.sizeOnly || group === 'nm_size'));

  // Drag a header to reorder columns; the order is remembered with the column choice.
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const dragProps = (key) => ({
    draggable: true,
    onDragStart: (e) => { setDrag(key); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', key); },
    onDragOver: (e) => { if (drag) { e.preventDefault(); if (over !== key) setOver(key); } },
    onDragLeave: () => setOver((v) => (v === key ? null : v)),
    onDrop: (e) => { e.preventDefault(); if (drag) saveCols(moveColumn(cols, drag, key)); setDrag(null); setOver(null); },
    onDragEnd: () => { setDrag(null); setOver(null); },
  });
  const dropSide = (key) => (drag && over === key && drag !== key ? (cols.indexOf(drag) < cols.indexOf(key) ? 'right' : 'left') : null);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = report.products.map(withRoi).filter((p) => !q || String(p.nm_id).includes(q) || (p.vendor_code || '').toLowerCase().includes(q) || (p.title || '').toLowerCase().includes(q));
    if (group === 'nm_size' && size !== 'all') list = list.filter((p) => (p.tech_size || '') === size);
    if (filter === 'sold') list = list.filter((p) => p.sold > 0);
    if (filter === 'no_cost') list = list.filter((p) => p.units_without_cost > 0 || p.cost_price == null);
    if (filter === 'loss') list = list.filter((p) => p.profit != null && Number(p.profit) < 0);
    if (filter === 'returns') list = list.filter((p) => p.returned > 0);
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string' && Number.isNaN(Number(av))) return av.localeCompare(bv, 'ru') * dir;
      return (Number(av) - Number(bv)) * dir;
    });
  }, [report.products, query, filter, size, group, sort]);

  // Sizes found in the loaded rows; shown only when the table is split by size.
  const sizes = useMemo(() => (group === 'nm_size'
    ? [...new Set(report.products.map((p) => p.tech_size || ''))].sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }))
    : []), [report.products, group]);

  const exportXlsx = () => {
    const header = ['Артикул WB', 'Название', ...visible.map((c) => c.label)];
    const body = rows.map((p) => [p.nm_id, p.title, ...visible.map((c) => (p[c.key] == null ? '' : c.money || c.pct || c.num ? Number(p[c.key]) : p[c.key]))]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Товары');
    XLSX.writeFile(wb, `marqit_wb_товары_${report.period.from}_${report.period.to}.xlsx`);
  };

  const cell = (p, c) => {
    const v = p[c.key];
    if (c.key === 'profit' && v == null) {
      return <Tooltip title="Не задана себестоимость: прибыль по товару не рассчитывается"><Box component="span" tabIndex={0} sx={{ color: tokens.inkMuted }}>нет себест.</Box></Tooltip>;
    }
    if (c.key === 'cost_price' && v == null) return <Box component="span" sx={{ color: tokens.warning }}>не задана</Box>;
    if (c.detail && v == null) {
      let why = group === 'nm_size' ? 'WB не делит эти данные по размерам — выберите «Размер: все вместе»' : DETAIL_MISSING[c.detail];
      if (c.key === 'drr' && p.ad_spend != null) why = 'Нет выручки за период: ДРР не рассчитывается';
      return <Tooltip title={why}><Box component="span" tabIndex={0} sx={{ color: tokens.inkMuted }}>—</Box></Tooltip>;
    }
    if (v == null) return '—';
    if (c.money) return formatMoney(v);
    if (c.pct) return formatPercent(v);
    if (c.num) return formatNumber(v);
    return v || '—';
  };

  return (
    <Card>
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
          <TextField size="small" placeholder="Поиск: артикул, название" value={query} onChange={(e) => setQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
            inputProps={{ 'aria-label': 'Поиск товара' }} sx={{ minWidth: 260 }} />
          <Select size="small" value={filter} onChange={(e) => setFilter(e.target.value)} inputProps={{ 'aria-label': 'Фильтр товаров' }}>
            <MenuItem value="all">Все товары</MenuItem>
            <MenuItem value="sold">С продажами</MenuItem>
            <MenuItem value="returns">С возвратами</MenuItem>
            <MenuItem value="no_cost">Без себестоимости</MenuItem>
            <MenuItem value="loss">Убыточные</MenuItem>
          </Select>
          <Select size="small" value={group === 'nm_size' ? (size === 'all' ? 'split' : size) : 'all'}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'all') { setSize('all'); onGroup('nm'); } else { setSize(v === 'split' ? 'all' : v); onGroup('nm_size'); }
            }}
            inputProps={{ 'aria-label': 'Размер' }} renderValue={(v) => `Размер: ${v === 'all' ? 'все вместе' : v === 'split' ? 'по каждому' : (v || 'без размера')}`}>
            <MenuItem value="all">Все размеры вместе (одна строка на товар)</MenuItem>
            <MenuItem value="split">По каждому размеру отдельно</MenuItem>
            {sizes.map((z) => <MenuItem key={z || 'none'} value={z}>Только размер: {z || 'без размера'}</MenuItem>)}
          </Select>
          <InfoTooltip title={EXPLAIN.size.short} heading={EXPLAIN.size.title} details={EXPLAIN.size.details} />
          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" size="small" startIcon={<ViewColumnOutlinedIcon />} onClick={(e) => setColMenu(e.currentTarget)}>Колонки</Button>
          <ExportGate><Button variant="outlined" size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={exportXlsx} disabled={!rows.length}>Экспорт XLSX</Button></ExportGate>
          <Menu anchorEl={colMenu} open={Boolean(colMenu)} onClose={() => setColMenu(null)}>
            {PRODUCT_COLUMNS.map((c) => (
              <MenuItem key={c.key} dense onClick={() => saveCols(cols.includes(c.key) ? cols.filter((k) => k !== c.key) : [...cols, c.key])}>
                <FormControlLabel control={<Checkbox size="small" checked={cols.includes(c.key)} />} label={c.label} sx={{ pointerEvents: 'none' }} />
              </MenuItem>
            ))}
            <MenuItem dense onClick={() => saveCols(PRODUCT_COLUMNS.filter((c) => c.default).map((c) => c.key))}>
              <Typography variant="body2" color="primary">Сбросить по умолчанию</Typography>
            </MenuItem>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ px: 2, pt: 0.5, pb: 1, maxWidth: 260 }}>
              Порядок колонок меняется перетаскиванием заголовка в таблице и запоминается.
            </Typography>
          </Menu>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Продажи, возвраты и выручка — по операциям финансовых отчётов WB за период; заказы и отмены — по дате заказа (оперативные данные).
          % выкупа = продажи / (продажи + возвраты) по операциям периода; это не доля заказов периода.
          Расходы без привязки к товару ({formatMoney(report.headline.unattributed_expenses)}) не распределяются по артикулам.
          {' '}{detailCaption(report.product_details, report.period)}
        </Typography>
        {group === 'nm' && <AdsCheckStrip check={report.ads_check} />}
        {rows.length === 0 ? (
          <EmptyState title="Товаров не найдено" description={report.products.length ? 'Измените поиск или фильтр.' : 'За выбранный период нет операций по товарам.'} />
        ) : (
          <Box sx={{ overflowX: 'auto', border: `1px solid ${tokens.border}`, borderRadius: 3 }}>
            <Table size="small" stickyHeader aria-label="Товары" sx={{ '& td, & th': { py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 240, ...STICKY_HEAD }}>
                    <TableSortLabel active={sort.key === 'title'} direction={sort.key === 'title' ? sort.dir : 'asc'}
                      onClick={() => setSort({ key: 'title', dir: sort.key === 'title' && sort.dir === 'asc' ? 'desc' : 'asc' })}>
                      Товар
                    </TableSortLabel>
                  </TableCell>
                  {visible.map((c) => (
                    <TableCell key={c.key} align={c.money || c.num || c.pct ? 'right' : 'left'} {...dragProps(c.key)}
                      title="Перетащите, чтобы поменять колонки местами"
                      sx={{
                        cursor: drag ? 'grabbing' : 'grab', userSelect: 'none', transition: 'opacity .15s, background-color .15s',
                        opacity: drag === c.key ? 0.45 : 1,
                        ...(dropSide(c.key) && {
                          bgcolor: tokens.primaryTint,
                          boxShadow: `inset ${dropSide(c.key) === 'left' ? 3 : -3}px 0 0 ${tokens.primary}`,
                        }),
                        '&:hover .col-grip': { opacity: 1 },
                      }}>
                      <DragIndicatorRoundedIcon className="col-grip" aria-hidden
                        sx={{ fontSize: 14, color: tokens.inkMuted, opacity: 0, transition: 'opacity .15s', verticalAlign: 'middle', mr: 0.25 }} />
                      <TableSortLabel active={sort.key === c.key} direction={sort.key === c.key ? sort.dir : 'desc'}
                        onClick={() => setSort({ key: c.key, dir: sort.key === c.key && sort.dir === 'desc' ? 'asc' : 'desc' })}>
                        {c.label}
                      </TableSortLabel>
                      {c.hint && <InfoTooltip title={c.hint} size={14} exclamation />}
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, 500).map((p) => (
                  <TableRow key={`${p.nm_id}-${p.tech_size || ''}`} hover sx={{ '&:hover .sticky-cell': { bgcolor: tokens.primaryTint } }}>
                    <TableCell className="sticky-cell" sx={STICKY_CELL}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar variant="rounded" src={p.photo_url || undefined} alt="" sx={{ width: 32, height: 42, bgcolor: tokens.primaryTint, color: tokens.inkMuted }}>
                          <ImageOutlinedIcon fontSize="small" />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, maxWidth: 220 }} noWrap title={p.title}>{p.title || 'Без названия'}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ maxWidth: 220 }}>WB {p.nm_id}{p.brand ? ` · ${p.brand}` : ''}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    {visible.map((c) => (
                      <TableCell key={c.key} align={c.money || c.num || c.pct ? 'right' : 'left'}
                        sx={{ whiteSpace: 'nowrap', ...(c.tone && p[c.key] != null && { color: toneColor(p[c.key], c.tone), fontWeight: c.tone === 'signed' ? 600 : undefined }) }}>
                        {cell(p, c)}
                      </TableCell>
                    ))}
                    <TableCell>
                      <Button size="small" startIcon={<ListAltRoundedIcon fontSize="small" />} onClick={() => onShowProduct(p)} sx={{ whiteSpace: 'nowrap' }}>Разбор товара</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {rows.length > 500 && <Typography variant="caption" color="text.secondary">Показаны первые 500 из {rows.length}. Уточните поиск или выгрузите все в XLSX.</Typography>}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- ABC analysis

const ABC_STYLE = {
  A: { bg: tokens.successSoft, fg: tokens.success, text: (g) => `первые 80% ${g}` },
  B: { bg: tokens.warningSoft, fg: tokens.warning, text: (g) => `следующие 15% ${g}` },
  C: { bg: tokens.primaryTint, fg: tokens.inkSoft, text: (g) => `остальные 5% ${g}, без продаж и в минусе` },
};
const ABC_METRICS = { profit: { label: 'Прибыль', genitive: 'прибыли' }, revenue: { label: 'Выручка', genitive: 'выручки' } };

const AbcChip = ({ cls }) => (
  <Chip size="small" label={cls} sx={{ fontWeight: 800, minWidth: 32, bgcolor: ABC_STYLE[cls].bg, color: ABC_STYLE[cls].fg }} />
);

const AbcView = ({ report, group, onShowProduct }) => {
  const [metric, setMetric] = useState('profit');
  const [cls, setCls] = useState('all');
  const [query, setQuery] = useState('');
  const { rows, summary, excluded, total } = useMemo(() => abcAnalysis(report.products, metric), [report.products, metric]);
  const m = ABC_METRICS[metric];
  const other = metric === 'profit' ? 'revenue' : 'profit';

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((p) => (cls === 'all' || p.abc === cls)
      && (!q || String(p.nm_id).includes(q) || (p.vendor_code || '').toLowerCase().includes(q) || (p.title || '').toLowerCase().includes(q)));
  }, [rows, cls, query]);

  const exportXlsx = () => {
    const num = (v) => (v == null ? '' : Number(v));
    const round1 = (v) => Math.round(v * 10) / 10;
    const header = ['Класс', 'Артикул WB', ...(group === 'nm_size' ? ['Размер'] : []), 'Артикул продавца', 'Название', `${m.label}, ₽`, `Доля ${m.genitive}, %`,
      'Накопленная доля, %', `${ABC_METRICS[other].label}, ₽`, 'Продажи, шт.', 'Маржинальность, %', 'Хранение (отчёт WB), ₽', 'Приёмка (отчёт WB), ₽',
      'Реклама (статистика WB), ₽', 'ДРР, %'];
    const body = rows.map((p) => [p.abc, p.nm_id, ...(group === 'nm_size' ? [p.tech_size || ''] : []), p.vendor_code || '', p.title || '', p.abc_value,
      round1(p.abc_share), round1(p.abc_cumulative), num(p[other]), p.sold, num(p.margin), num(p.paid_storage), num(p.paid_acceptance),
      num(p.ad_spend), num(p.drr)]);
    const totals = [
      ['Класс', 'Товаров', 'Доля товаров, %', `${m.label}, ₽`, `Доля ${m.genitive}, %`],
      ...ABC_CLASSES.map((c) => [c, summary[c].count, round1(summary[c].itemsShare), summary[c].value, round1(summary[c].share)]),
      [],
      ['Период', `${formatDate(report.period.from)} – ${formatDate(report.period.to)}`],
      ['Правило', `A — товары, дающие первые 80% ${m.genitive}; B — следующие 15%; C — остальные 5%, товары без продаж и убыточные`],
      ['Реклама', 'Расход по статистике рекламных кампаний WB, справочно: в прибыль по товару не входит; ДРР = реклама / выручка'],
      ...(excluded.length ? [['Без себестоимости (не участвуют)', excluded.length]] : []),
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([header, ...body]), 'ABC-анализ');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(totals), 'Итоги');
    XLSX.writeFile(wb, `marqit_wb_abc_${metric}_${report.period.from}_${report.period.to}.xlsx`);
  };

  return (
    <Card>
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
          <ToggleButtonGroup size="small" exclusive value={metric} onChange={(_, v) => v && setMetric(v)} aria-label="Показатель ABC-анализа">
            <ToggleButton value="profit">По прибыли</ToggleButton>
            <ToggleButton value="revenue">По выручке</ToggleButton>
          </ToggleButtonGroup>
          <TextField size="small" placeholder="Поиск: артикул, название" value={query} onChange={(e) => setQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
            inputProps={{ 'aria-label': 'Поиск товара в ABC-анализе' }} sx={{ minWidth: 240 }} />
          <Box sx={{ flex: 1 }} />
          <ExportGate><Button variant="outlined" size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={exportXlsx} disabled={!rows.length}>Выгрузить в Excel</Button></ExportGate>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
          {ABC_CLASSES.map((c) => {
            const active = cls === c;
            const st = ABC_STYLE[c];
            return (
              <Box key={c} component="button" type="button" onClick={() => setCls(active ? 'all' : c)} aria-pressed={active}
                sx={{ textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit', p: 2, borderRadius: '14px', bgcolor: tokens.paper,
                  border: `${active ? 2 : 1}px solid ${active ? st.fg : tokens.border}`, '&:hover': { boxShadow: tokens.shadowRaised } }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: st.bg, color: st.fg, fontWeight: 800, fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{c}</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700 }}>{formatNumber(summary[c].count)} {pluralize(summary[c].count, 'товар', 'товара', 'товаров')} · {formatPercent(summary[c].itemsShare)}</Typography>
                    <Typography variant="caption" color="text.secondary">{st.text(m.genitive)}</Typography>
                  </Box>
                </Stack>
                <Typography sx={{ mt: 1, fontWeight: 700, fontSize: '1.25rem' }}>{formatMoney(summary[c].value)}</Typography>
                <Typography variant="body2" color="text.secondary">{formatPercent(summary[c].share)} {m.genitive} за период</Typography>
              </Box>
            );
          })}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Товары упорядочены по {m.genitive} за период ({formatMoney(total)} по товарам с положительным значением). A — дают первые 80%, B — следующие 15%,
          C — остальные 5%, а также товары без продаж и убыточные. Нажмите на класс, чтобы оставить только его товары.
          {metric === 'profit' && excluded.length > 0 && ` Без себестоимости прибыль не рассчитывается — ${formatNumber(excluded.length)} товаров в анализ по прибыли не вошли.`}
        </Typography>

        {rows.length === 0 ? (
          <EmptyState title={metric === 'profit' ? 'Нет товаров с рассчитанной прибылью' : 'Нет товаров за период'}
            description={metric === 'profit' ? 'Задайте себестоимость товаров или постройте анализ по выручке.' : 'За выбранный период нет операций по товарам.'}
            action={metric === 'profit' ? <Button component={RouterLink} to={mpPath('wildberries', 'costs')}>Заполнить себестоимость</Button> : null} />
        ) : (
          <Box sx={{ overflowX: 'auto', border: `1px solid ${tokens.border}`, borderRadius: '12px' }}>
            <Table size="small" stickyHeader aria-label="ABC-анализ товаров">
              <TableHead>
                <TableRow>
                  <TableCell>Класс</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>Товар</TableCell>
                  <TableCell align="right">{m.label}</TableCell>
                  <TableCell align="right">Доля</TableCell>
                  <TableCell align="right">Накопленная доля</TableCell>
                  <TableCell align="right">{ABC_METRICS[other].label}</TableCell>
                  <TableCell align="right">Продажи</TableCell>
                  <TableCell align="right">Хранение (отчёт WB)</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.slice(0, 500).map((p) => (
                  <TableRow key={`${p.nm_id}-${p.tech_size || ''}`} hover>
                    <TableCell><AbcChip cls={p.abc} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar variant="rounded" src={p.photo_url || undefined} alt="" sx={{ width: 34, height: 44, bgcolor: tokens.primaryTint, color: tokens.inkMuted }}>
                          <ImageOutlinedIcon fontSize="small" />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, maxWidth: 220 }} noWrap title={p.title}>{p.title || 'Без названия'}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap component="div">WB {p.nm_id}{p.tech_size ? ` · ${p.tech_size}` : ''}{p.vendor_code ? ` · ${p.vendor_code}` : ''}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: p.abc_value < 0 ? tokens.negative : undefined }}>{formatMoney(p.abc_value)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatPercent(p.abc_share)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatPercent(p.abc_cumulative)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{p[other] == null ? '—' : formatMoney(p[other])}</TableCell>
                    <TableCell align="right">{formatNumber(p.sold)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{p.paid_storage == null ? '—' : formatMoney(p.paid_storage)}</TableCell>
                    <TableCell>
                      <Tooltip title="Разбор товара">
                        <IconButton size="small" onClick={() => onShowProduct(p)} aria-label="Разбор товара">
                          <ListAltRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {visible.length > 500 && <Typography variant="caption" color="text.secondary">Показаны первые 500 из {visible.length}. Полный список — в выгрузке Excel.</Typography>}
      </CardContent>
    </Card>
  );
};

const PRODUCT_VIEWS = [
  { key: 'table', label: 'Все товары' },
  { key: 'abc', label: 'ABC-анализ' },
];

const ProductsTab = (props) => {
  const [view, setView] = useState('table');
  return (
    <>
      <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)} aria-label="Вид списка товаров" sx={{ mb: 2, bgcolor: tokens.paper }}>
        {PRODUCT_VIEWS.map((v) => <ToggleButton key={v.key} value={v.key} sx={{ px: 2 }}>{v.label}</ToggleButton>)}
      </ToggleButtonGroup>
      {view === 'table' ? <ProductsTable {...props} /> : <AbcView {...props} />}
    </>
  );
};

// ---------------------------------------------------------------- penalties / reconciliation

const PenaltiesTab = ({ params }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  // the page belongs to the period and cabinet it was chosen for: new params start at page 0
  const [paging, setPaging] = useState({ params, page: 0 });
  const page = paging.params === params ? paging.page : 0;
  const setPage = (p) => setPaging({ params, page: p });
  const [attempt, setAttempt] = useState(0);
  const load = () => setAttempt((n) => n + 1);
  useEffect(() => {
    let live = true;
    setData(null);
    setError('');
    analyticsApi.operations({ ...params, filter: 'penalty', limit: 50, offset: page * 50 })
      .then((r) => live && setData(r.data)).catch((e) => live && setError(apiError(e)));
    return () => { live = false; };
  }, [params, page, attempt]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Skeleton variant="rounded" height={240} />;
  return (
    <Card>
      <CardContent>
        <Typography variant="h3" sx={{ mb: 0.5 }}>Штрафы</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Каждая строка — операция из отчёта реализации WB с полем «Общая сумма штрафов». Причина — из столбца «Виды доставок, штрафов и корректировок ВВ».
          Хранение и приёмка к штрафам не относятся и показаны в своих разделах.
        </Typography>
        {data.items.length === 0 ? (
          <EmptyState title="Штрафов нет" description="За выбранный период в отчётах WB нет штрафов." />
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Дата</TableCell>
                  <TableCell align="right">Сумма</TableCell>
                  <TableCell>Причина от WB</TableCell>
                  <TableCell>Пояснение</TableCell>
                  <TableCell>Товар / заказ / поставка</TableCell>
                  <TableCell>Отчёт</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((o) => (
                  <TableRow key={o.rrd_id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(o.date)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{formatMoney(o.penalty, { kopecks: true })}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      {o.reason || <Typography variant="body2" color="text.secondary">WB не передал причину</Typography>}
                      <Typography variant="caption" color="text.secondary" display="block">{o.operation}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      {o.explanation || <Typography variant="body2" color="text.secondary">См. формулировку WB</Typography>}
                    </TableCell>
                    <TableCell>
                      {o.nm_id ? <Typography variant="body2">{o.title || o.vendor_code || 'Товар'} · WB {o.nm_id}</Typography> : <Typography variant="body2" color="text.secondary">без товара</Typography>}
                      {o.srid && <Typography variant="caption" color="text.secondary" display="block">Заказ {o.srid}</Typography>}
                      {o.supply_id > 0 && <Typography variant="caption" color="text.secondary" display="block">Поставка {o.supply_id}</Typography>}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>№ {o.report_id}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data.total > 50 && (
              <TablePagination component="div" count={data.total} page={page} rowsPerPage={50} rowsPerPageOptions={[50]}
                onPageChange={(_, p) => setPage(p)} labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`} />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const ReconciliationTab = ({ params }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const load = () => setAttempt((n) => n + 1);
  useEffect(() => {
    let live = true;
    setRows(null);
    setError('');
    analyticsApi.reconciliation(params).then((r) => live && setRows(r.data)).catch((e) => live && setError(apiError(e)));
    return () => { live = false; };
  }, [params, attempt]);
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!rows) return <Skeleton variant="rounded" height={240} />;
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="h3">Сверка с итогами WB</Typography>
          <InfoTooltip exclamation size={20} title={EXPLAIN.reconciliation.short} heading={EXPLAIN.reconciliation.title} details={EXPLAIN.reconciliation.details} sources={EXPLAIN.reconciliation.sources} />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Для каждого загруженного отчёта суммы, рассчитанные MARQIT по строкам детализации, сравниваются с итогами, которые передаёт WB в списке отчётов.
          Расхождение показывается как есть и не скрывается.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Как читать: «WB» — число из сводки отчёта в вашем кабинете WB. «MARQIT» — та же сумма, которую мы пересчитали по строкам детализации этого отчёта.
          «Разница» = MARQIT минус WB. Если всё совпадает, мы загрузили и правильно разобрали каждую строку отчёта. Если есть разница, это значит одно из двух: не все строки отчёта загрузились,
          или WB учёл какую-то операцию иначе, чем мы. Показываются только те показатели, где есть разница.
        </Typography>
        {rows.length === 0 ? <EmptyState title="Нет загруженных отчётов за период" /> : (
          <Stack spacing={1.5}>
            {rows.map((r) => (
              <Box key={`${r.report_id}-${r.period}`} sx={{ border: `1px solid ${tokens.border}`, borderRadius: 3, p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={1}>
                  <Typography variant="subtitle1">
                    Отчёт № {r.report_id} · {r.period === 'daily' ? 'ежедневный' : 'еженедельный'} · {formatDate(r.date_from)}–{formatDate(r.date_to)}
                  </Typography>
                  {r.matches
                    ? <Chip size="small" icon={<CheckCircleRoundedIcon />} label="Совпадает" sx={{ bgcolor: tokens.successSoft, color: tokens.success }} />
                    : <Chip size="small" icon={<WarningAmberRoundedIcon />} label="Есть расхождения" sx={{ bgcolor: tokens.warningSoft, color: tokens.warning }} />}
                </Stack>
                <Typography variant="caption" color="text.secondary">Строк: {formatNumber(r.rows)}</Typography>
                {!r.matches && (
                  <Table size="small" sx={{ mt: 1 }}>
                    <TableHead><TableRow><TableCell>Показатель</TableCell><TableCell align="right">WB</TableCell><TableCell align="right">MARQIT</TableCell><TableCell align="right">Разница</TableCell></TableRow></TableHead>
                    <TableBody>
                      {r.checks.filter((c) => c.diff != null && Number(c.diff) !== 0).map((c) => (
                        <TableRow key={c.key}>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <span>{c.label}</span>
                              {EXPLAIN.checks[c.key] && (
                                <InfoTooltip title={EXPLAIN.checks[c.key].short} heading={c.label} details={[EXPLAIN.checks[c.key].t]} sources={EXPLAIN.checks[c.key].src} />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">{formatMoney(c.wb, { kopecks: true })}</TableCell>
                          <TableCell align="right">{formatMoney(c.computed, { kopecks: true })}</TableCell>
                          <TableCell align="right" sx={{ color: tokens.warning, fontWeight: 600 }}>{formatMoney(c.diff, { kopecks: true })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                {!r.matches && (
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                    Что делать: сверьте строку в таблице выше с сводкой отчёта № {r.report_id} в кабинете WB (Финансы → Отчёты реализации). Если разница небольшая и совпадает с одной статьёй сводки,
                    значит дело в том, как эта статья учтена; если строк в детализации WB больше, чем «Строк» выше, откройте «Состояние загрузки» и дождитесь полной загрузки.
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- page

// ---------------------------------------------------------------- Ozon

const OZON_TABS = [
  { key: 'summary', label: 'Показатели и расходы' },
  { key: 'products', label: 'Товары' },
];

const OZON_HEADLINE = [
  { key: 'net_profit', label: 'Итоговая прибыль', hint: 'К перечислению − себестоимость − налоги. Комиссия и услуги Ozon уже вычтены из суммы к перечислению.' },
  { key: 'revenue', label: 'Выручка', hint: '«Реализовано на сумму» из отчёта о реализации Ozon: доставленные товары минус возвращённые, за полные месяцы периода.' },
  { key: 'to_pay', label: 'К перечислению', hint: '«Итого к начислению» по отчёту о реализации + компенсации − логистика, возвраты, размещение, продвижение, эквайринг и прочие услуги из финансового отчёта Ozon.' },
  { key: 'commission', label: 'Комиссия Ozon', hint: 'Выручка − «Итого к начислению»: вознаграждение Ozon за вычетом его выплат по программам лояльности.' },
  { key: 'logistics', label: 'Логистика и возвраты', hint: 'Доставка, обработка отправлений, обработка возвратов и обратная логистика из финансового отчёта Ozon.' },
  { key: 'storage', label: 'Размещение', hint: 'Услуга размещения товаров на складах Ozon.' },
  { key: 'other_expenses', label: 'Продвижение и прочие услуги', hint: 'Продвижение и реклама, эквайринг и прочие услуги Ozon из финансового отчёта.' },
  { key: 'cost_of_goods', label: 'Себестоимость', hint: 'Себестоимость SKU на первый день месяца × проданные штуки − возвращённые.' },
];

const OzonCoverage = ({ report }) => {
  const c = report.coverage;
  if (c.full) return null;
  const op = report.operational;
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      Ozon публикует отчёт о реализации раз в месяц, до 5-го числа следующего месяца, поэтому выручка, расходы и прибыль считаются только за полные месяцы.{' '}
      {c.finance_from
        ? `Финансовые показатели — за ${formatRange(c.finance_from, c.finance_to)}. `
        : 'В выбранном периоде нет полного месяца с вышедшим отчётом — выберите, например, прошлый месяц. '}
      {op && op.sales > 0
        ? `За ${formatRange(c.uncovered_from, c.uncovered_to)} доставлено заказов: ${formatNumber(op.sales)} шт. на ${formatMoney(op.revenue)} (по дате заказа, в расчёт не входят).`
        : ''}
    </Alert>
  );
};

const OZON_PRODUCT_COLUMNS = [
  { key: 'orders', label: 'Заказы', num: true },
  { key: 'cancelled', label: 'Отмены', num: true },
  { key: 'sold', label: 'Продажи', num: true },
  { key: 'returned', label: 'Возвраты', num: true },
  { key: 'revenue', label: 'Выручка', money: true },
  { key: 'commission', label: 'Комиссия', money: true },
  { key: 'for_pay', label: 'К начислению', money: true },
  { key: 'cost_of_goods', label: 'Себестоимость продаж', money: true },
  { key: 'profit', label: 'Прибыль', money: true },
  { key: 'margin', label: 'Маржа', pct: true },
  { key: 'stock', label: 'Остаток', num: true },
];

const OzonProducts = ({ products }) => {
  if (!products.length) return <EmptyState title="Нет товаров за период" description="За выбранный период нет ни заказов, ни продаж по отчётам Ozon." />;
  const cell = (p, col) => {
    const v = p[col.key];
    if (v == null) return '—';
    if (col.money) return formatMoney(v);
    if (col.pct) return formatPercent(v);
    return formatNumber(v);
  };
  return (
    <Card>
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Продажи, выручка и комиссия — из отчётов о реализации за полные месяцы периода; заказы и отмены — по дате заказа за весь период; остаток — текущий.
          Прибыль товара = к начислению − себестоимость: услуги Ozon (логистика, размещение, продвижение) к товарам не привязаны и вычитаются только в итоговой прибыли.
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Товары Ozon">
            <TableHead>
              <TableRow>
                <TableCell sx={STICKY_HEAD}>Товар</TableCell>
                {OZON_PRODUCT_COLUMNS.map((c) => <TableCell key={c.key} align="right" sx={{ whiteSpace: 'nowrap' }}>{c.label}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.nm_id}>
                  <TableCell sx={{ ...STICKY_CELL, minWidth: 240 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar variant="rounded" src={p.photo_url || undefined} alt="" sx={{ width: 40, height: 40, bgcolor: tokens.primaryTint }}>
                        <ImageOutlinedIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, maxWidth: 260 }} noWrap title={p.title}>{p.title || p.vendor_code || `SKU ${p.nm_id}`}</Typography>
                        <Typography variant="caption" color="text.secondary">SKU {p.nm_id}{p.vendor_code ? ` · ${p.vendor_code}` : ''}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  {OZON_PRODUCT_COLUMNS.map((c) => <TableCell key={c.key} align="right" sx={{ whiteSpace: 'nowrap' }}>{cell(p, c)}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
};

const OzonReport = ({ report, tab, onTab }) => {
  const h = report.headline;
  const value = (key) => (key === 'logistics' ? h.logistics : h[key]);
  return (
    <>
      <SubTabs items={OZON_TABS} value={tab === 'products' ? 'products' : 'summary'} onChange={onTab} sx={{ mb: 3 }} />
      <OzonCoverage report={report} />
      {tab === 'products' ? <OzonProducts products={report.products} /> : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {OZON_HEADLINE.map((m) => {
              const v = value(m.key);
              const isProfit = m.key === 'net_profit';
              return (
                <Grid key={m.key} item xs={12} sm={6} md={3}>
                  <Card sx={{ height: '100%', ...(isProfit ? { background: tokens.heroGradient } : {}) }}>
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="body2" color="text.secondary">{m.label}</Typography>
                        <InfoTooltip title={m.hint} />
                      </Stack>
                      <Typography sx={{ fontWeight: 700, fontSize: isProfit ? '1.6rem' : '1.35rem', mt: 0.5, whiteSpace: 'nowrap', color: RESULT_KEYS.includes(m.key) ? toneColor(v) : Number(v) < 0 ? tokens.negative : tokens.ink }}>
                        {formatMoney(v)}
                      </Typography>
                      {m.key === 'commission' && h.commission_percent != null && (
                        <Typography variant="body2" color="text.secondary">{formatPercent(h.commission_percent)} от выручки</Typography>
                      )}
                      {isProfit && !h.profit_final && (
                        <Typography variant="body2" sx={{ color: tokens.warning, mt: 0.5 }}>
                          Предварительно: {h.profit_missing.map((x) => MISSING_LABELS[x] || x).join('; ')}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={8}>
              <Card>
                <CardContent>
                  <Typography variant="h3" sx={{ mb: 0.5 }}>Детализация</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Нажмите на статью, чтобы увидеть расшифровку по операциям финансового отчёта Ozon.
                  </Typography>
                  {report.sections.map((s) => <SectionRow key={s.key} s={s} onShowOps={() => {}} />)}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Card>
                <CardContent>
                  <Typography variant="h3" sx={{ mb: 1.5 }}>Продажи</Typography>
                  {[
                    ['Продано, шт.', formatNumber(h.sold)],
                    ['Возвращено, шт.', formatNumber(h.returned)],
                    ['Процент выкупа', h.buyout_percent == null ? '—' : formatPercent(h.buyout_percent)],
                    ['Товаров без себестоимости', formatNumber(h.articles_without_cost)],
                    ['Услуги без привязки к товару', formatMoney(h.unattributed_expenses)],
                  ].map(([k, v]) => (
                    <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.75, borderBottom: `1px solid ${tokens.border}` }}>
                      <Typography variant="body2" color="text.secondary">{k}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{v}</Typography>
                    </Stack>
                  ))}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    По отчётам о реализации Ozon за полные месяцы периода. Отмены и невыкупы в отчёт не входят.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

const TABS = [
  { key: 'summary', label: 'Показатели и расходы' },
  { key: 'products', label: 'Товары' },
  { key: 'penalties', label: 'Штрафы' },
  { key: 'reconciliation', label: 'Сверка с WB' },
];

const MarketplaceAnalyticsPage = () => {
  const { marketplace, accounts: mpAccounts } = useOutletContext();
  const location = useLocation();
  const navigate = useNavigate();
  const q = new URLSearchParams(location.search);
  const period = useMemo(() => periodFromSearch(location.search), [location.search]);
  const account = q.get('account') || '';
  const tab = q.get('tab') || 'summary';
  const group = q.get('group') === 'nm_size' ? 'nm_size' : 'nm';

  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ops, setOps] = useState(null);
  const [product, setProduct] = useState(null);

  const params = useMemo(() => ({ ...periodQuery(period), ...(account ? { account_id: account } : {}) }), [period, account]);

  const setQuery = (patch) => {
    const next = new URLSearchParams(location.search);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    navigate({ search: next.toString() }, { replace: true });
  };

  // only the latest request may fill the page: a slower answer for an older period is dropped
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    if (marketplace !== 'wildberries' && marketplace !== 'ozon') return;
    const seq = ++loadSeq.current;
    setLoading(true);
    setError('');
    try {
      const { data } = await analyticsApi.master({ ...params, group, ...(marketplace === 'ozon' ? { marketplace: 'ozon' } : {}) });
      if (seq === loadSeq.current) setReport(data);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setError(e?.response?.status === 404 ? `Кабинет ${marketplace === 'ozon' ? 'Ozon' : 'Wildberries'} не найден. Подключите кабинет на странице «Подключения».` : apiError(e));
      setReport(null);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [marketplace, params, group]);

  useEffect(() => { load(); }, [load]);

  const showOps = (filter, title, nmId) => setOps({ title, params: { ...params, filter, ...(nmId ? { nm_id: nmId } : {}) } });

  const header = (
    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 3 }}>
      <Box>
        {mpAccounts.length > 1 ? (
          <Select size="small" value={account || 'all'} onChange={(e) => setQuery({ account: e.target.value === 'all' ? '' : e.target.value })}
            inputProps={{ 'aria-label': 'Кабинет' }} sx={{ minWidth: 240 }}>
            <MenuItem value="all">Все кабинеты ({mpAccounts.length})</MenuItem>
            {mpAccounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
          </Select>
        ) : (
          <Typography color="text.secondary">Кабинет: <b>{mpAccounts[0]?.name || '—'}</b></Typography>
        )}
      </Box>
      <PeriodPicker value={period} onChange={(p) => navigate({ search: periodSearch(p, Object.fromEntries([...q.entries()].filter(([k]) => !['preset', 'from', 'to'].includes(k)))) }, { replace: true })} />
    </Stack>
  );

  if (marketplace === 'ozon') {
    return (
      <Box>
        {header}
        {error && <ErrorState message={error} onRetry={load} />}
        {!report && loading && <Stack spacing={2}><Skeleton variant="rounded" height={200} /><Skeleton variant="rounded" height={320} /></Stack>}
        {report && (
          <Box sx={{ opacity: loading ? 0.6 : 1 }} aria-busy={loading}>
            <OzonReport report={report} tab={tab} onTab={(v) => setQuery({ tab: v === 'summary' ? '' : v })} />
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          Данные: отчёты о реализации, финансовые отчёты, заказы и остатки Ozon, загруженные на сервер MARQIT.{' '}
          <Link component={RouterLink} to={`/marketplaces/sync/${account || mpAccounts[0]?.id || ''}`}>Состояние загрузки</Link>
        </Typography>
      </Box>
    );
  }

  if (marketplace !== 'wildberries') {
    return <ComingSoon marketplace={marketplace} what="Аналитика" />;
  }

  const h = report?.headline;
  return (
    <Box>
      {header}

      <SubTabs items={TABS} value={tab} onChange={(v) => setQuery({ tab: v === 'summary' ? '' : v })} sx={{ mb: 3 }} />

      {error && <ErrorState message={error} onRetry={load} />}
      {!report && loading && <Stack spacing={2}><Skeleton variant="rounded" height={200} /><Skeleton variant="rounded" height={320} /></Stack>}

      {report && (
        <Box sx={{ opacity: loading ? 0.6 : 1 }} aria-busy={loading}>
          {(!report.coverage.full || report.other_currencies?.length > 0) && (
            <Alert severity="info" sx={{ mb: 2 }}
              action={<InfoTooltip exclamation size={20} title={EXPLAIN.coverage.short} heading={EXPLAIN.coverage.title} details={EXPLAIN.coverage.details} sources={EXPLAIN.coverage.sources} />}>
              {report.coverage.finance_to
                ? `Финансовые отчёты WB загружены по ${formatDate(report.coverage.finance_to)}. `
                : 'Финансовые отчёты WB за период ещё не загружены. '}
              {report.operational && (report.operational.sales > 0)
                ? `За ${formatRange(report.coverage.uncovered_from, report.coverage.uncovered_to)} есть оперативные продажи: ${formatNumber(report.operational.sales)} шт. на ${formatMoney(report.operational.revenue)} (в расчёт не входят). `
                : ''}
              {report.other_currencies?.map((c) => `${formatNumber(c.rows)} операций в ${c.currency} не включены в суммы. `)}
            </Alert>
          )}

          {tab === 'summary' && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {HEADLINE.map((m, idx) => {
                  const v = headlineValue(h, m.key);
                  const isProfit = m.key === 'net_profit';
                  return (
                    <Grid key={m.key} item xs={12} sm={6} md={idx < 4 ? 6 : 4} lg={isProfit ? 4 : idx < 4 ? 8 / 3 : 2}>
                      <Card sx={{ height: '100%', ...(isProfit ? { background: tokens.heroGradient } : {}) }}>
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body2" color="text.secondary">{m.label}</Typography>
                            <InfoTooltip title={m.hint} />
                          </Stack>
                          <Typography sx={{ fontWeight: 700, fontSize: isProfit ? '2rem' : '1.35rem', mt: 0.5, whiteSpace: 'nowrap', color: RESULT_KEYS.includes(m.key) ? toneColor(v) : Number(v) < 0 ? tokens.negative : tokens.ink }}>
                            {formatMoney(v)}
                          </Typography>
                          {m.key === 'commission' && h.commission_percent != null && (
                            <Typography variant="body2" color="text.secondary">{formatPercent(h.commission_percent)} от выручки {formatMoney(h.commission_base)}</Typography>
                          )}
                          {isProfit && !h.profit_final && (
                            <Typography variant="body2" sx={{ color: tokens.warning, mt: 0.5 }}>
                              Предварительно: {h.profit_missing.map((x) => MISSING_LABELS[x] || x).join('; ')}
                            </Typography>
                          )}
                          {isProfit && h.profit_final && <Typography variant="body2" color="text.secondary">Все исходные данные заданы</Typography>}
                          {m.key === 'realized' && Number(h.wb_discounts) !== 0 && (
                            <Typography variant="body2" color="text.secondary">скидки WB за её счёт: {formatMoney(h.wb_discounts)}</Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={8}>
                  <Card>
                    <CardContent>
                      <Typography variant="h3" sx={{ mb: 0.5 }}>Детализация</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Нажмите на статью, чтобы увидеть расшифровку по формулировкам WB и исходные операции.
                      </Typography>
                      {report.sections.map((s) => <SectionRow key={s.key} s={s} onShowOps={showOps} />)}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h3" sx={{ mb: 1.5 }}>Продажи</Typography>
                      {[
                        ['Продано, шт.', formatNumber(h.sold)],
                        ['Возвращено, шт.', formatNumber(h.returned)],
                        ['Процент выкупа', h.buyout_percent == null ? '—' : formatPercent(h.buyout_percent)],
                        ['Товаров без себестоимости', formatNumber(h.articles_without_cost)],
                        ['Реклама со «Счёта» продвижения', formatMoney(h.ad_account)],
                        ['Расходы без привязки к товару', formatMoney(h.unattributed_expenses)],
                      ].map(([k, v]) => (
                        <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.75, borderBottom: `1px solid ${tokens.border}` }}>
                          <Typography variant="body2" color="text.secondary">{k}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{v}</Typography>
                        </Stack>
                      ))}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Процент выкупа = продажи / (продажи + возвраты) по операциям отчёта за период.
                      </Typography>
                      {h.articles_without_cost > 0 && (
                        <Button component={RouterLink} to={mpPath('wildberries', 'costs')} size="small" sx={{ mt: 1, px: 0 }}>Заполнить себестоимость</Button>
                      )}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Typography variant="h3" sx={{ mb: 1 }}>Отчёт за период</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Еженедельные отчёты WB загружаются автоматически. Там же можно собрать отчёт с выбранными полями и скачать его.
                      </Typography>
                      <Button variant="outlined" component={RouterLink} to={mpPath('wildberries', 'reports')}>Открыть отчёты</Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}

          {tab === 'products' && <ProductsTab report={report} group={group} onGroup={(g) => setQuery({ group: g === 'nm' ? '' : g })} onShowOps={showOps} onShowProduct={setProduct} />}
          {tab === 'penalties' && <PenaltiesTab params={params} />}
          {tab === 'reconciliation' && <ReconciliationTab params={params} />}
        </Box>
      )}

      <ProductDrawer product={product} params={params} onClose={() => setProduct(null)} />
      <OperationsDialog open={Boolean(ops)} onClose={() => setOps(null)} params={ops?.params} title={ops?.title || ''} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
        Данные: отчёты реализации, продажи, заказы и расходы Wildberries, загруженные на сервер MARQIT.{' '}
        <Link component={RouterLink} to={`/marketplaces/sync/${account || mpAccounts[0]?.id || ''}`}>Состояние загрузки</Link>
      </Typography>
    </Box>
  );
};

export default MarketplaceAnalyticsPage;
